export const RELAY_DATA_ADDON_PLAN_IDENTIFIER = 'pre' as const
export const ALLPLATFORM_QUOTE_PLAN_IDENTIFIER = 'allplatform_quote' as const
export const HOME_QUOTE_PLAN_IDENTIFIER = 'home_quote' as const

export type DataAddonProductIdentifier = typeof RELAY_DATA_ADDON_PLAN_IDENTIFIER | typeof ALLPLATFORM_QUOTE_PLAN_IDENTIFIER | typeof HOME_QUOTE_PLAN_IDENTIFIER

export function isDataAddonPricingPending(status: unknown): boolean {
  return status === 'idle' || status === 'loading'
}

export interface RelayDataAddonPolicy {
  price_eur_cents_per_gb: number
  min_purchase_gb: number
  period_purchase_limit_gb: number
  full_quota_gb?: number
}

export interface PackageDataAddonPolicy {
  kind: 'packages'
  packages: Array<{
    quota_gb: number
    price_eur_cents: number
  }>
}

export interface PerGbDataAddonPolicy {
  kind: 'per_gb'
  price_eur_cents_per_gb: number
  min_purchase_gb: number
  max_purchase_gb: number
}

export interface DataAddonPolicyRecord {
  plan_id: number
  updated_at: string
  data_addon_policy: {
    standard?: PackageDataAddonPolicy | PerGbDataAddonPolicy
    relay?: RelayDataAddonPolicy
  }
}

export interface ParsedDataAddonPolicies {
  policies: Record<string, DataAddonPolicyRecord>
  errors: Record<string, string>
}

export interface DataAddonServiceLike {
  id?: unknown
  cate?: unknown
  plan_id?: unknown
  status?: unknown
  quote?: unknown
  used?: unknown
  billing_mode?: unknown
  period_start?: unknown
  period_end?: unknown
  extra?: {
    buy_time?: unknown
    buy_length?: unknown
    data_addon_relay_period?: {
      period_key?: unknown
      purchased_gb?: unknown
    } | null
  } | null
}

export type RelayDataAddonPeriodContext = Readonly<{
  planId: number
  periodKey: string
  sourceKey: string
}>

export type RelayDataAddonPeriodResponse = Readonly<{
  planId: number
  periodKey: string
  purchasedGb: number
}>

export type DataAddonOffer =
  | {
      ok: true
      kind: 'per_gb'
      product: DataAddonProductIdentifier
      planId: number
      unitPriceCents: number
      minPurchaseGb: number
      maxPurchaseGb: number
      periodPurchaseLimitGb?: number
      fullQuotaGb?: number
    }
  | {
      ok: true
      kind: 'packages'
      product: typeof ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
      planId: number
      packages: PackageDataAddonPolicy['packages']
    }
  | {
      ok: false
      product: DataAddonProductIdentifier
      planId: number
      reason: 'invalid_policy' | 'missing_policy' | 'unsupported_product'
      detail?: string
    }

export type DataAddonQuote =
  | {
      ok: true
      product: DataAddonProductIdentifier
      planId: number
      quantityGb: number
      requestQuantityGb: number
      chargeableQuantityGb: number
      subtotalCents: number
      allPeriods: boolean
      offer: Extract<DataAddonOffer, { ok: true }>
    }
  | {
      ok: false
      product: DataAddonProductIdentifier
      planId: number
      reason: Extract<DataAddonOffer, { ok: false }>['reason'] | 'invalid_quantity' | 'below_minimum' | 'above_maximum' | 'package_unavailable' | 'all_periods_unsupported'
      detail?: string
      offer?: Extract<DataAddonOffer, { ok: true }>
    }

export type DataAddonRequestContext = Readonly<{
  mode: 'buyService'
  product: DataAddonProductIdentifier
  planId: number
  amount: number
  allPre: boolean
  promoCode: string
}>

type UnknownRecord = Record<string, unknown>

const SHANGHAI_UTC_OFFSET_MS = 8 * 60 * 60 * 1000
const MAX_TIMER_DELAY_MS = 2_147_000_000

function sameShanghaiMonth(firstMs: number, secondMs: number): boolean {
  const first = new Date(firstMs + SHANGHAI_UTC_OFFSET_MS)
  const second = new Date(secondMs + SHANGHAI_UTC_OFFSET_MS)
  return first.getUTCFullYear() === second.getUTCFullYear() && first.getUTCMonth() === second.getUTCMonth()
}

function dateInputMs(value: number | Date): number {
  return value instanceof Date ? value.getTime() : Number(value)
}

function shanghaiCalendarMonthKey(timeMs: number): string {
  const shifted = new Date(timeMs + SHANGHAI_UTC_OFFSET_MS)
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  return `${shifted.getUTCFullYear()}-${month}`
}

function nextShanghaiMonthBoundaryMs(timeMs: number): number {
  const shifted = new Date(timeMs + SHANGHAI_UTC_OFFSET_MS)
  return Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 1) - SHANGHAI_UTC_OFFSET_MS
}

function periodSourceValue(value: unknown): string {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? `date:${value.toISOString()}` : 'date:invalid'
  }
  return `${typeof value}:${String(value ?? '')}`
}

export function relayDataAddonPeriodSourceKey(service: DataAddonServiceLike | null | undefined): string {
  return JSON.stringify([
    periodSourceValue(service?.id),
    periodSourceValue(service?.cate),
    periodSourceValue(service?.plan_id),
    periodSourceValue(service?.billing_mode),
    periodSourceValue(service?.period_start),
    periodSourceValue(service?.period_end),
    periodSourceValue(service?.extra?.buy_time),
    periodSourceValue(service?.extra?.data_addon_relay_period?.period_key),
    periodSourceValue(service?.extra?.data_addon_relay_period?.purchased_gb),
  ])
}

export function dataAddonPeriodEnd(service: DataAddonServiceLike, nowInput: number | Date = Date.now()): Date {
  if (service.billing_mode === 'anchor') {
    if (service.period_end == null || (typeof service.period_end === 'string' && service.period_end.trim().length === 0)) {
      throw new Error('Invalid anchor period_end for data add-on validity')
    }
    const periodEnd = new Date(service.period_end as string | number | Date)
    if (!Number.isFinite(periodEnd.getTime())) {
      throw new Error('Invalid anchor period_end for data add-on validity')
    }
    return periodEnd
  }
  const now = dateInputMs(nowInput)
  if (!Number.isFinite(now)) {
    throw new Error('Invalid current period time for data add-on validity')
  }
  return new Date(nextShanghaiMonthBoundaryMs(now))
}

export function relayDataAddonPeriodKey(service: DataAddonServiceLike, nowInput: number | Date = Date.now()): string {
  if (service.billing_mode === 'anchor' && service.period_end != null) {
    if (service.period_start == null || (typeof service.period_start === 'string' && service.period_start.trim().length === 0)) {
      throw new Error('Invalid anchor period_start for relay add-on purchase')
    }
    const periodStart = new Date(service.period_start as string | number | Date)
    if (!Number.isFinite(periodStart.getTime())) {
      throw new Error('Invalid anchor period_start for relay add-on purchase')
    }
    return `anchor:${periodStart.toISOString()}`
  }
  const now = dateInputMs(nowInput)
  if (!Number.isFinite(now)) {
    throw new Error('Invalid current period time for relay add-on purchase')
  }
  return `calendar:${shanghaiCalendarMonthKey(now)}`
}

export function createRelayDataAddonPeriodContext(service: DataAddonServiceLike, nowInput: number | Date = Date.now()): RelayDataAddonPeriodContext {
  const planId = Number(service.plan_id)
  if (!Number.isSafeInteger(planId) || ![2, 4].includes(planId)) {
    throw new Error('Invalid relay data add-on plan')
  }
  return Object.freeze({
    planId,
    periodKey: relayDataAddonPeriodKey(service, nowInput),
    sourceKey: relayDataAddonPeriodSourceKey(service),
  })
}

export function relayDataAddonPeriodWatchKey(service: DataAddonServiceLike | null | undefined, nowInput: number | Date = Date.now()): string {
  const sourceKey = relayDataAddonPeriodSourceKey(service)
  if (!service) return JSON.stringify([sourceKey, null])
  try {
    return JSON.stringify([sourceKey, createRelayDataAddonPeriodContext(service, nowInput).periodKey])
  } catch {
    return JSON.stringify([sourceKey, null])
  }
}

export function relayDataAddonPeriodRefreshDelayMs(nowInput: number | Date = Date.now()): number {
  const now = dateInputMs(nowInput)
  if (!Number.isFinite(now)) {
    throw new Error('Invalid current period time for relay add-on refresh')
  }
  const nextBoundary = nextShanghaiMonthBoundaryMs(now)
  return Math.max(1, Math.min(nextBoundary - now, MAX_TIMER_DELAY_MS))
}

export function sameRelayDataAddonPeriodContext(first: RelayDataAddonPeriodContext, second: RelayDataAddonPeriodContext): boolean {
  return first.planId === second.planId && first.periodKey === second.periodKey && first.sourceKey === second.sourceKey
}

export function canAcceptRelayDataAddonPeriodResponse(
  requestId: number,
  latestRequestId: number,
  expected: RelayDataAddonPeriodContext,
  current: RelayDataAddonPeriodContext | null,
): boolean {
  return requestId === latestRequestId && current != null && sameRelayDataAddonPeriodContext(expected, current)
}

export function hasExhaustedStandardDataQuota(
  service: DataAddonServiceLike | null | undefined,
): boolean {
  const quote = Number(service?.quote)
  const used = Number(service?.used)
  return quote > 0 && used >= quote
}

export function isDataAddonServiceEligible(service: DataAddonServiceLike | null | undefined, product: DataAddonProductIdentifier, nowInput: number | Date = Date.now()): boolean {
  if (!service) return false
  if (product === RELAY_DATA_ADDON_PLAN_IDENTIFIER) return service.status === 'on'
  if (product !== ALLPLATFORM_QUOTE_PLAN_IDENTIFIER && product !== HOME_QUOTE_PLAN_IDENTIFIER) {
    return false
  }
  if (
    product === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
    && (service.cate !== 'allplatform' || Number(service.plan_id) !== 4)
  ) return false
  if (
    product === HOME_QUOTE_PLAN_IDENTIFIER
    && (service.cate !== 'home' || ![5, 6].includes(Number(service.plan_id)))
  ) return false
  if (service.status !== 'on' && service.status !== 'off') return false

  const now = nowInput instanceof Date ? nowInput.getTime() : Number(nowInput)
  if (!Number.isFinite(now)) return false
  const buyTimeInput = service.extra?.buy_time
  if (buyTimeInput == null) return false
  const buyTime = new Date(buyTimeInput as string | number).getTime()
  if (!Number.isFinite(buyTime)) return false
  // Legacy multi-month services retain their original buy_time; status=on is
  // the authority that their current natural-month period is still active.
  if (service.status === 'on' && service.billing_mode !== 'anchor') return true
  let periodActive: boolean
  if (service.billing_mode === 'anchor') {
    const periodEnd = new Date(service.period_end as string | number | Date).getTime()
    periodActive = Number.isFinite(periodEnd) && periodEnd > now
  } else {
    periodActive = sameShanghaiMonth(buyTime, now)
  }
  if (!periodActive) return false
  if (service.status === 'on') return true

  if (product === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER) {
    const buyLength = Number(service.extra?.buy_length)
    if (!Number.isInteger(buyLength) || buyLength <= 1) return false
  }

  return hasExhaustedStandardDataQuota(service)
}

export function canBuyAllplatformStandardData(
  service: DataAddonServiceLike | null | undefined,
  nowInput: number | Date = Date.now(),
): boolean {
  return isDataAddonServiceEligible(service, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, nowInput)
}

export function canRestoreAllplatformServiceWithStandardData(
  service: DataAddonServiceLike | null | undefined,
  nowInput: number | Date = Date.now(),
): boolean {
  return service?.status === 'off'
    && canBuyAllplatformStandardData(service, nowInput)
}

export function canBuyHomeStandardData(
  service: DataAddonServiceLike | null | undefined,
  nowInput: number | Date = Date.now(),
): boolean {
  return isDataAddonServiceEligible(service, HOME_QUOTE_PLAN_IDENTIFIER, nowInput)
}

export function canRestoreHomeServiceWithStandardData(
  service: DataAddonServiceLike | null | undefined,
  nowInput: number | Date = Date.now(),
): boolean {
  return service?.status === 'off'
    && canBuyHomeStandardData(service, nowInput)
}

export function resolveDataAddonProductForService(
  service: DataAddonServiceLike | null | undefined,
  requestedProduct: DataAddonProductIdentifier,
  nowInput: number | Date = Date.now(),
): DataAddonProductIdentifier {
  if (isDataAddonServiceEligible(service, requestedProduct, nowInput)) {
    return requestedProduct
  }
  if (
    requestedProduct === RELAY_DATA_ADDON_PLAN_IDENTIFIER
    && isDataAddonServiceEligible(service, ALLPLATFORM_QUOTE_PLAN_IDENTIFIER, nowInput)
  ) {
    return ALLPLATFORM_QUOTE_PLAN_IDENTIFIER
  }
  return requestedProduct
}

export function selectDataAddonService<T extends DataAddonServiceLike>(
  services: readonly T[] | null | undefined,
  selection: {
    cate: string
    planId: number
    product: DataAddonProductIdentifier
    fallbackProduct?: DataAddonProductIdentifier
    now?: number | Date
  },
): T | undefined {
  const exactServices = services?.filter(
    service => service.cate === selection.cate && Number(service.plan_id) === selection.planId,
  )
  const selected = exactServices?.find(service =>
    isDataAddonServiceEligible(service, selection.product, selection.now))
  if (selected || selection.fallbackProduct == null) return selected
  return exactServices?.find(service =>
    isDataAddonServiceEligible(service, selection.fallbackProduct!, selection.now))
}

function asRecord(value: unknown, name: string): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${name} must be an object`)
  }
  return value as UnknownRecord
}

export function parseRelayDataAddonPeriodResponse(value: unknown, expected: RelayDataAddonPeriodContext): RelayDataAddonPeriodResponse {
  const response = asRecord(value, 'relay data add-on period state')
  if (
    typeof response.addon_plan_id !== 'number' ||
    response.addon_plan_id !== expected.planId ||
    typeof response.period_key !== 'string' ||
    response.period_key.length === 0 ||
    response.period_key !== expected.periodKey ||
    typeof response.allplatform !== 'number' ||
    !Number.isSafeInteger(response.allplatform) ||
    response.allplatform < 0
  ) {
    throw new Error('Invalid relay data add-on period state.')
  }
  return Object.freeze({
    planId: response.addon_plan_id,
    periodKey: response.period_key,
    purchasedGb: response.allplatform,
  })
}

function assertExactKeys(value: UnknownRecord, keys: string[], name: string): void {
  const actual = Object.keys(value).sort()
  const expected = [...keys].sort()
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new Error(`${name} must contain exactly ${expected.join(', ')}`)
  }
}

function positiveInteger(value: unknown, name: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    throw new Error(`${name} must be a positive integer`)
  }
  return value
}

function parseRelayPolicy(value: unknown, requireFullQuota: boolean): RelayDataAddonPolicy {
  const policy = asRecord(value, 'relay')
  assertExactKeys(
    policy,
    requireFullQuota
      ? ['price_eur_cents_per_gb', 'min_purchase_gb', 'period_purchase_limit_gb', 'full_quota_gb']
      : ['price_eur_cents_per_gb', 'min_purchase_gb', 'period_purchase_limit_gb'],
    'relay',
  )
  const parsed: RelayDataAddonPolicy = {
    price_eur_cents_per_gb: positiveInteger(policy.price_eur_cents_per_gb, 'relay.price_eur_cents_per_gb'),
    min_purchase_gb: positiveInteger(policy.min_purchase_gb, 'relay.min_purchase_gb'),
    period_purchase_limit_gb: positiveInteger(policy.period_purchase_limit_gb, 'relay.period_purchase_limit_gb'),
  }
  if (parsed.min_purchase_gb > parsed.period_purchase_limit_gb) {
    throw new Error('relay.min_purchase_gb must not exceed relay.period_purchase_limit_gb')
  }
  if (requireFullQuota) {
    parsed.full_quota_gb = positiveInteger(policy.full_quota_gb, 'relay.full_quota_gb')
    if (parsed.full_quota_gb < parsed.period_purchase_limit_gb) {
      throw new Error('relay.full_quota_gb must be at least relay.period_purchase_limit_gb')
    }
  }
  return parsed
}

function parsePackagePolicy(value: unknown): PackageDataAddonPolicy {
  const policy = asRecord(value, 'standard')
  assertExactKeys(policy, ['kind', 'packages'], 'standard')
  if (policy.kind !== 'packages') {
    throw new Error('standard.kind must be packages')
  }
  if (!Array.isArray(policy.packages) || policy.packages.length !== 2) {
    throw new Error('standard.packages must contain exactly two packages')
  }
  const packages = policy.packages.map((entry, index) => {
    const packageValue = asRecord(entry, `standard.packages[${index}]`)
    assertExactKeys(packageValue, ['quota_gb', 'price_eur_cents'], `standard.packages[${index}]`)
    return {
      quota_gb: positiveInteger(packageValue.quota_gb, `standard.packages[${index}].quota_gb`),
      price_eur_cents: positiveInteger(packageValue.price_eur_cents, `standard.packages[${index}].price_eur_cents`),
    }
  })

  for (let index = 1; index < packages.length; index += 1) {
    if (packages[index].quota_gb <= packages[index - 1].quota_gb) {
      throw new Error('standard.packages quota_gb values must be strictly increasing')
    }
    if (packages[index].price_eur_cents <= packages[index - 1].price_eur_cents) {
      throw new Error('standard.packages price_eur_cents values must be strictly increasing')
    }
  }
  return { kind: 'packages', packages }
}

function parsePerGbPolicy(value: unknown): PerGbDataAddonPolicy {
  const policy = asRecord(value, 'standard')
  assertExactKeys(policy, ['kind', 'price_eur_cents_per_gb', 'min_purchase_gb', 'max_purchase_gb'], 'standard')
  if (policy.kind !== 'per_gb') {
    throw new Error('standard.kind must be per_gb')
  }
  const parsed: PerGbDataAddonPolicy = {
    kind: 'per_gb',
    price_eur_cents_per_gb: positiveInteger(policy.price_eur_cents_per_gb, 'standard.price_eur_cents_per_gb'),
    min_purchase_gb: positiveInteger(policy.min_purchase_gb, 'standard.min_purchase_gb'),
    max_purchase_gb: positiveInteger(policy.max_purchase_gb, 'standard.max_purchase_gb'),
  }
  if (parsed.max_purchase_gb < parsed.min_purchase_gb) {
    throw new Error('standard.max_purchase_gb must be at least standard.min_purchase_gb')
  }
  return parsed
}

function parsePolicyRecord(key: string, value: unknown): DataAddonPolicyRecord {
  const record = asRecord(value, `data_addon_policies.${key}`)
  assertExactKeys(record, ['plan_id', 'updated_at', 'data_addon_policy'], `data_addon_policies.${key}`)
  const planId = positiveInteger(record.plan_id, `data_addon_policies.${key}.plan_id`)
  if (String(planId) !== key) {
    throw new Error(`plan_id ${planId} does not match policy key ${key}`)
  }
  if (typeof record.updated_at !== 'string' || Number.isNaN(Date.parse(record.updated_at)) || new Date(record.updated_at).toISOString() !== record.updated_at) {
    throw new Error('updated_at must be a canonical ISO-8601 timestamp')
  }
  const policy = asRecord(record.data_addon_policy, 'data_addon_policy')
  const normalized: DataAddonPolicyRecord = {
    plan_id: planId,
    updated_at: record.updated_at,
    data_addon_policy: {},
  }

  if (planId === 2) {
    assertExactKeys(policy, ['relay'], 'data_addon_policy')
    normalized.data_addon_policy.relay = parseRelayPolicy(policy.relay, false)
  } else if (planId === 4) {
    assertExactKeys(policy, ['standard', 'relay'], 'data_addon_policy')
    normalized.data_addon_policy.standard = parsePackagePolicy(policy.standard)
    normalized.data_addon_policy.relay = parseRelayPolicy(policy.relay, true)
  } else if (planId === 5 || planId === 6) {
    assertExactKeys(policy, ['standard'], 'data_addon_policy')
    normalized.data_addon_policy.standard = parsePerGbPolicy(policy.standard)
  } else {
    throw new Error(`unsupported data add-on plan_id ${planId}`)
  }

  return normalized
}

export function parseDataAddonPolicies(value: unknown): ParsedDataAddonPolicies {
  const parsed: ParsedDataAddonPolicies = { policies: {}, errors: {} }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    parsed.errors._root = 'data_addon_policies must be an object'
    return parsed
  }

  const requiredKeys = ['2', '4', '5', '6']
  const actualKeys = Object.keys(value).sort()
  if (actualKeys.length !== requiredKeys.length || actualKeys.some((key, index) => key !== requiredKeys[index])) {
    parsed.errors._root = 'data add-on policy catalog must contain exactly plans 2, 4, 5, and 6'
    return parsed
  }

  for (const key of requiredKeys) {
    try {
      parsed.policies[key] = parsePolicyRecord(key, (value as UnknownRecord)[key])
    } catch (error) {
      parsed.errors[key] = error instanceof Error ? error.message : String(error)
    }
  }
  if (Object.keys(parsed.errors).length > 0) {
    parsed.policies = {}
    parsed.errors._root = 'data add-on policy catalog is invalid'
  }
  return parsed
}

export function resolveDataAddonOffer(parsed: ParsedDataAddonPolicies, planIdInput: number | string | null | undefined, product: DataAddonProductIdentifier): DataAddonOffer {
  const planId = Number(planIdInput)
  const policyKey = Number.isInteger(planId) ? String(planId) : String(planIdInput ?? '')
  const invalidDetail = parsed.errors[policyKey]
  if (invalidDetail) {
    return { ok: false, product, planId, reason: 'invalid_policy', detail: invalidDetail }
  }
  const record = parsed.policies[policyKey]
  if (!record) {
    return { ok: false, product, planId, reason: 'missing_policy' }
  }

  if (product === RELAY_DATA_ADDON_PLAN_IDENTIFIER) {
    const relay = record.data_addon_policy.relay
    if (!relay) {
      return { ok: false, product, planId, reason: 'unsupported_product' }
    }
    return {
      ok: true,
      kind: 'per_gb',
      product,
      planId,
      unitPriceCents: relay.price_eur_cents_per_gb,
      minPurchaseGb: relay.min_purchase_gb,
      maxPurchaseGb: Number.MAX_SAFE_INTEGER,
      periodPurchaseLimitGb: relay.period_purchase_limit_gb,
      fullQuotaGb: relay.full_quota_gb,
    }
  }

  if (product === ALLPLATFORM_QUOTE_PLAN_IDENTIFIER) {
    const standard = record.data_addon_policy.standard
    if (planId !== 4 || standard?.kind !== 'packages') {
      return { ok: false, product, planId, reason: 'unsupported_product' }
    }
    return {
      ok: true,
      kind: 'packages',
      product,
      planId,
      packages: standard.packages,
    }
  }

  const standard = record.data_addon_policy.standard
  if (![5, 6].includes(planId) || standard?.kind !== 'per_gb') {
    return { ok: false, product, planId, reason: 'unsupported_product' }
  }
  return {
    ok: true,
    kind: 'per_gb',
    product,
    planId,
    unitPriceCents: standard.price_eur_cents_per_gb,
    minPurchaseGb: standard.min_purchase_gb,
    maxPurchaseGb: standard.max_purchase_gb,
  }
}

export function quoteDataAddonSelection(input: {
  parsed: ParsedDataAddonPolicies
  planId: number | string | null | undefined
  product: DataAddonProductIdentifier
  quantityGb: number
  alreadyPurchasedGb?: number
  allPeriods?: boolean
  serviceBuyLength?: number
  alreadyFullPeriods?: number
}): DataAddonQuote {
  const offer = resolveDataAddonOffer(input.parsed, input.planId, input.product)
  if (!offer.ok) return offer

  const quantityGb = Number(input.quantityGb)
  if (!Number.isSafeInteger(quantityGb) || quantityGb <= 0) {
    return {
      ok: false,
      product: input.product,
      planId: offer.planId,
      reason: 'invalid_quantity',
      offer,
    }
  }

  if (offer.kind === 'packages') {
    const selectedPackage = offer.packages.find((entry) => entry.quota_gb === quantityGb)
    if (!selectedPackage) {
      return {
        ok: false,
        product: input.product,
        planId: offer.planId,
        reason: 'package_unavailable',
        offer,
      }
    }
    return {
      ok: true,
      product: input.product,
      planId: offer.planId,
      quantityGb,
      requestQuantityGb: quantityGb,
      chargeableQuantityGb: quantityGb,
      subtotalCents: selectedPackage.price_eur_cents,
      allPeriods: false,
      offer,
    }
  }

  const alreadyPurchasedGb = Math.max(0, Number(input.alreadyPurchasedGb) || 0)
  const isRelay = input.product === RELAY_DATA_ADDON_PLAN_IDENTIFIER
  const maxPurchaseGb = offer.maxPurchaseGb

  if (input.allPeriods) {
    if (!isRelay || !offer.fullQuotaGb || !offer.periodPurchaseLimitGb) {
      return {
        ok: false,
        product: input.product,
        planId: offer.planId,
        reason: 'all_periods_unsupported',
        offer,
      }
    }
    const serviceBuyLength = Number(input.serviceBuyLength)
    const alreadyFullPeriods = Number(input.alreadyFullPeriods ?? 0)
    if (!Number.isSafeInteger(serviceBuyLength) || serviceBuyLength <= 0 || !Number.isSafeInteger(alreadyFullPeriods) || alreadyFullPeriods < 0) {
      return {
        ok: false,
        product: input.product,
        planId: offer.planId,
        reason: 'invalid_quantity',
        offer,
      }
    }
    const periodsToBuy = serviceBuyLength - alreadyFullPeriods
    const currentPeriodCreditGb = alreadyFullPeriods === 0
      ? Math.min(alreadyPurchasedGb, offer.periodPurchaseLimitGb)
      : 0
    const chargeableQuantityGb = offer.periodPurchaseLimitGb * periodsToBuy - currentPeriodCreditGb
    if (chargeableQuantityGb <= 0) {
      return {
        ok: false,
        product: input.product,
        planId: offer.planId,
        reason: 'above_maximum',
        offer,
      }
    }
    return {
      ok: true,
      product: input.product,
      planId: offer.planId,
      quantityGb,
      requestQuantityGb: offer.periodPurchaseLimitGb,
      chargeableQuantityGb,
      subtotalCents: chargeableQuantityGb * offer.unitPriceCents,
      allPeriods: true,
      offer,
    }
  }

  if (!Number.isSafeInteger(quantityGb * offer.unitPriceCents)) {
    return { ok: false, product: input.product, planId: offer.planId, reason: 'invalid_quantity', offer }
  }

  if (quantityGb < offer.minPurchaseGb) {
    return {
      ok: false,
      product: input.product,
      planId: offer.planId,
      reason: 'below_minimum',
      offer,
    }
  }
  if (quantityGb > maxPurchaseGb) {
    return {
      ok: false,
      product: input.product,
      planId: offer.planId,
      reason: 'above_maximum',
      offer,
    }
  }

  return {
    ok: true,
    product: input.product,
    planId: offer.planId,
    quantityGb,
    requestQuantityGb: quantityGb,
    chargeableQuantityGb: quantityGb,
    subtotalCents: quantityGb * offer.unitPriceCents,
    allPeriods: false,
    offer,
  }
}

export function formatDataAddonQuotePrice(quote: DataAddonQuote, currency: string): string {
  if (!quote.ok) return '—'
  if (quote.offer.kind === 'packages' || quote.allPeriods) {
    return `${currency}${(quote.subtotalCents / 100).toFixed(2)}`
  }
  return `${currency}${(quote.offer.unitPriceCents / 100).toFixed(2)}/GB`
}

function requirePurchasableQuote(quote: DataAddonQuote): Extract<DataAddonQuote, { ok: true }> {
  if (!quote.ok) {
    throw new Error(`Data add-on is not purchasable: ${quote.reason}`)
  }
  return quote
}

export function createDataAddonRequestContext(quoteInput: DataAddonQuote, options: { promoCode?: string } = {}): DataAddonRequestContext {
  const quote = requirePurchasableQuote(quoteInput)
  return Object.freeze({
    mode: 'buyService' as const,
    product: quote.product,
    planId: quote.planId,
    amount: quote.requestQuantityGb,
    allPre: quote.allPeriods,
    promoCode: options.promoCode || '',
  })
}

export function buildDataAddonPurchaseFields(request: DataAddonRequestContext, options: { dryRun: boolean }) {
  return {
    dry_run: options.dryRun,
    plan_id: request.product,
    promocode: request.promoCode,
    buy_length: request.amount,
    all_pre: request.allPre,
    addon_plan_id: request.planId,
  }
}

export function buildDataAddonAutoBuyFields(request: DataAddonRequestContext) {
  return {
    auto_buy: request.product,
    auto_buy_month: request.amount,
    auto_buy_promo: request.promoCode,
    all_pre: request.allPre,
    addon_plan_id: request.planId,
  }
}

export function defaultDataAddonQuantity(offer: DataAddonOffer): number | null {
  if (!offer.ok) return null
  if (offer.kind === 'packages') return offer.packages[0]?.quota_gb ?? null
  return offer.minPurchaseGb
}
